import _ from 'lodash';
import { addDays, isBefore, isWithinRange, parse, subDays } from 'date-fns';
import { MEDICAID_UNWINDING_EXTENSION_LOSS_DATE, MEDICAID_UNWINDING_EXTENSION_BEGIN_DATE, MEDICAID_UNWINDING_EXTENSION_END_DATE } from 'routes/marketplace-ede/constants/ede/eligibility';
import { slashDateToISO } from 'routes/marketplace-ede/web-ui/utils/format';
import {
  isMemberRequestingCoverage,
  isMemberPreliminarilyMedicaidEligible,
} from 'routes/marketplace-ede/utils/selectors';

import { EdeMemberType } from 'routes/marketplace-ede/types/ede/edeMember';

const UICG_213_KEY = 'hasLost';

export const LOSS_OF_MEC_PATH = 'attestations.other.changeInCircumstance.LOSS_OF_MEC.changeDate';
export const FUTURE_LOSS_OF_MEC_PATH = 'attestations.other.changeInCircumstance.FUTURE_LOSS_OF_MEC.changeDate';
export const MEDICAID_END_INDICATOR_PATH = 'attestations.medicaid.medicaidEndIndicator';
export type Question = {
  followUp: Record<string, string>;
  index: number;
  InfoModal: string;
  key: string;
  path: string;
  qeKey: string;
  question: Record<string, string>;
  since: string;
};

export const meetsMedicaidUnwindingRequirements = (medicaidEndDate: string | null): boolean => {
  const now = new Date();
  const yesterday = subDays(now, 1);

  if (isBefore(now, MEDICAID_UNWINDING_EXTENSION_BEGIN_DATE) || !medicaidEndDate) return false;

  const cleanedMedicaidEndDate = parse(slashDateToISO(medicaidEndDate));

  const isWithinExtensionRange = isWithinRange(
    cleanedMedicaidEndDate,
    MEDICAID_UNWINDING_EXTENSION_LOSS_DATE,
    yesterday,
  );
  const medicaidUnwindingAllowed = isWithinRange(
    now,
    MEDICAID_UNWINDING_EXTENSION_BEGIN_DATE,
    MEDICAID_UNWINDING_EXTENSION_END_DATE,
  );
  return isWithinExtensionRange && medicaidUnwindingAllowed;
};

export const isValidFutureLossOfMecDate = (changeDateStr: string | null): boolean => {
  if (!changeDateStr) return false;

  const now = new Date();
  const sixtyDaysFromNow = addDays(now, 60);
  const changeDate = parse(slashDateToISO(changeDateStr));

  return isWithinRange(changeDate, now, sixtyDaysFromNow);
};

export const lossOfQualifyingHealthCoverageMembers = (
  members: EdeMemberType[],
  question: Question,
  isCr84Enabled: boolean,
): EdeMemberType[] => {
  const isEligibleMember = (member: EdeMemberType) =>
    isMemberRequestingCoverage(member) ||
    isMemberPreliminarilyMedicaidEligible(member);

  return members.filter((member) => {
    const medicaidEndDate = _.get(member, LOSS_OF_MEC_PATH, null);
    const futureLossOfMecDate = _.get(member, FUTURE_LOSS_OF_MEC_PATH, null);
    const medicaidEndIndicator = _.get(member, MEDICAID_END_INDICATOR_PATH, null);

    if (question.qeKey === UICG_213_KEY) {
      if (isCr84Enabled) {
        // Display member if either:
        // 1. Consumer is requesting coverage AND is preliminarily QHP or APTC eligible AND
        // 2. medicaidEndIndicator is false/null, OR
        // 3. medicaidEndIndicator is true AND future loss date is valid (within 60 days)
        return (
          isEligibleMember(member) &&
            (!medicaidEndIndicator ||
              (medicaidEndIndicator && isValidFutureLossOfMecDate(futureLossOfMecDate)))
        );
      }
      return isEligibleMember(member) && !meetsMedicaidUnwindingRequirements(medicaidEndDate);
    }
    return isEligibleMember(member);
  });
};

// eslint-disable-next-line arrow-body-style
export const findMemberCoverage60DayBeforeCircumstanceChangeConfirmedIndicator = (member: EdeMemberType) => {
  return member.computed.eligibleSEP?.find(
    sep =>
      !_.isNil(sep.coverage60DayBeforeCircumstanceChangeConfirmedIndicator),
  )?.coverage60DayBeforeCircumstanceChangeConfirmedIndicator;
};

// eslint-disable-next-line arrow-body-style
export const didMemberMoveFromForeignCountry = (member: EdeMemberType) => {
  return !!member.attestations.other?.changeInCircumstance?.RELOCATION?.movedFromForeignCountryIndicator;
};
